import { DateTime } from 'luxon';

import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router } from '@angular/router';

import { ButtonModule } from 'primeng/button';
import { InputOtpModule } from 'primeng/inputotp';
import { RippleModule } from 'primeng/ripple';

import { NgIconComponent } from '@ng-icons/core';

import { ErrorSmallComponent } from '@components/error-handling/error-small/error-small.component';

import { ROUTE_PATHS } from '@configs/route-paths.const';

import { ErrorStore, SessionTokenStore, UserStore } from '@cxc/core/services';
import { IdentityAuthAPI } from '@services/API/identity/auth.service';
import { UsersAPI } from '@services/API/identity/users.service';
import { FormService } from '@services/form.service';
import { NavigationService } from '@services/navigation.service';
import { AppConfigStore } from '@services/store/app-config.store';

import { CustomValidators } from '@cxc/core/utils';

@Component({
  selector: 'two-FA-page',
  templateUrl: './two-FA.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  styles: [
    `
      ::ng-deep p-inputotp.is-valid input {
        border: 2px solid #4caf50 !important;
      }
    `,
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ButtonModule,
    RippleModule,
    InputOtpModule,
    ErrorSmallComponent,
    NgIconComponent,
  ],
})
export class TwoFAPage implements OnInit, OnDestroy {
  formService = inject(FormService);
  errorStore = inject(ErrorStore);
  userStore = inject(UserStore);
  private sessionTokens = inject(SessionTokenStore);

  deadline!: DateTime;
  countdown = signal(60 * 3);

  twoFAForm!: FormGroup;
  public isValid = signal(false);

  private countdownIntervalId: ReturnType<typeof setInterval> | null = null;

  constructor(
    public navigationService: NavigationService,
    private identityAuthAPI: IdentityAuthAPI,
    private appConfigStore: AppConfigStore,
    private usersAPI: UsersAPI,

    private router: Router,
    fb: FormBuilder
  ) {
    this.twoFAForm = fb.group({
      twoFA: [
        '',
        CustomValidators.required(),
        // This breaks 2FA, disable for now
        //CustomValidators.minLength(5, 'verification code'),
      ],
    });
  }

  ngOnInit(): void {
    this.errorStore.clear();
    this.startTimer();
  }

  ngOnDestroy(): void {
    clearInterval(this.countdownIntervalId!);

    this.appConfigStore.patch({ allowRoutes: { '2FA': false } });

    // Left without verifying the code: drop the half-started login.
    if (this.sessionTokens.token() === null) {
      this.userStore.logOut();
    }
  }

  startTimer() {
    this.deadline = DateTime.now();
    this.deadline.plus({ minutes: 3 });

    this.countdownIntervalId = setInterval(() => {
      const diff =
        60 * 3 + Math.ceil(this.deadline.diffNow(['seconds']).seconds);
      this.countdown.set(diff);

      if (this.countdown() <= 0) {
        this.router.navigate(['/login']);
      }
    }, 1000);
  }

  verify() {
    if (this.formService.trySubmitForm(this.twoFAForm)) {
      return;
    }
    this.isValid.set(true);

    const twoFA = this.twoFAForm.value.twoFA;

    // Trying to log in

    this.identityAuthAPI
      .loginTwoFA({
        email: this.userStore.get().email!,
        session: this.userStore.get().session!,
        challenge_name: this.userStore.get().challenge_name!,
        code: twoFA,
      })
      .subscribe({
        next: twoFAResponse => {
          // LEN-2764: the access token stays in memory; the refresh token
          // is the HttpOnly cookie identity-api set on this response. The
          // Cognito challenge is spent.
          this.sessionTokens.set(twoFAResponse.access_token);
          this.userStore.patch({
            session: undefined,
            challenge_name: undefined,
          });

          this.usersAPI.get().subscribe({
            next: async userResponse => {
              this.userStore.patch({ ...userResponse, logged: true });

              // LEN-2764: land on the dashboard redirect (HomeRedirectComponent),
              // not on the first module with permissions: for the CXC
              // SUPER_ADMIN that is AIVA, the only module identity fills
              // permissions for. The redirect sends staff to Portfolio and
              // anyone without Portfolio to their first module.
              this.router.navigate([ROUTE_PATHS.DASHBOARD]);
            },
          });
        },
        error: () => {
          this.twoFAForm.controls['twoFA'].setValue('');
          this.isValid.set(false);
        },
      });
  }
}
